<template>
  <PForm
    ref="pForm"
    :show-header="false"
    :primary-cta="formState.edit ? updateButton : {}"
    :secondary-cta="{}"
    body-class="q-px-2x-md"
    @submit.prevent="onSubmit"
  >
    <div class="col-12 q-mt-lg">
      <span class="text-md text-weight-medium text-neutral">
        Tax Settings
      </span>
      <q-separator class="q-mb-sm" />
    </div>

    <div class="row q-col-gutter-xl">
      <div class="col-6">
        <PField v-model="isTaxApplicable" :label="taxLabel">
          <template #control>
            <POptionGroup
              v-model="isTaxApplicable"
              :options="[
                {
                  label: 'Yes',
                  value: true,
                },
                {
                  label: 'No',
                  value: false,
                },
              ]"
            />
          </template>
        </PField>
      </div>

      <div v-if="isTaxApplicable" class="col-6">
        <PSelectInput
          v-model="formData.tax_settings"
          :options="unref(taxFilter.entities)"
          label="Tax Selection"
          option-label="name"
          placeholder="Select a tax"
          required
          lazy-rules
          @filter="taxFilter.onFilter"
          @filter-abort="taxFilter.onFilterAbort"
        />
      </div>
      <div v-else class="col-6"></div>

      <div v-if="user?.basicInfo?.role === 'buyer'" class="col-6">
        <PInput
          v-model.number="formData.min_net_sales_margin"
          required
          label="Minimum Net Sales Margin"
          help="Expected minimum net sales margin (exc. gst liability) per invoice"
          class="prepend-bg"
          type="number"
          step="any"
          :validations="{ lte: 5, type: 'float' }"
        >
          <template #prepend>
            <span class="text-sm">%</span>
          </template>
        </PInput>
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { useQuasar } from "quasar";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { useFilter } from "src/composables/filter";
import taxSettingsForm from "../models/taxSettingsForm";
import * as taxApi from "src/api/tax";
const { organisation } = storeToRefs(useOrganisationStore());

const $q = useQuasar();

const pForm = ref(null);
const isTaxApplicable = ref(false);
let taxFilter = ref({});
let taxes = ref([]);

defineExpose({ pForm });

const { formState, user } = storeToRefs(useUserStore());
const { putUser, toggleUserForm } = useUserStore();

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const financeStepCompleted = inject("financeStepCompleted", () => false);

const mapTaxSettings = (organisation) => {
  const userRole = unref(user)?.role;
  const organisationId = unref(organisation)?._id;

  if (!unref(financeStepCompleted) && organisationId) {
    return unref(organisation).tax_settings.find((tax) =>
      tax.applicable.includes(userRole),
    );
  }

  return null;
};

const computedFinanceStepCompleted = computed(() => {
  return unref(formState).edit || unref(financeStepCompleted);
});

const { formData, isLoading, submitForm } = useForm({
  formModel: taxSettingsForm(mapTaxSettings(organisation)),
  entity: unref(user).taxSettingsInfo,
  formRef: pForm,
  isForm: formState,
  put: putUser,
  toggleForm: toggleUserForm,
  editMode: unref(computedFinanceStepCompleted),
});

const taxLabel = computed(() => {
  return unref(user)?.basicInfo?.role === "seller"
    ? "TDS Applicable?"
    : "TCS Applicable?";
});

const updateButton = computed(() => {
  return {
    loading: unref(isLoading),
    label: "Update",
    type: "update",
  };
});

const initTaxFilter = async (role) => {
  const response = await taxApi.index({
    params: {
      roles: role,
    },
  });

  taxes.value = response?.data?.data;

  taxFilter.value = useFilter({
    entities: taxes,
    entity: "tax",
  });
};

const onSubmit = async () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      text: `Are you sure you want to edit these details ?`,
      isFooterVisible: true,
      submittable: true,
      cancellable: true,
      closable: true,
      onConfirm: async () => {
        unref(formData).role = unref(user)?.basicInfo?.role;

        await submitForm(false);
      },
    },
  });
};

watch(
  () => unref(formData).tax_settings,
  (taxSettings) => {
    if (taxSettings?._id) {
      isTaxApplicable.value = true;
    } else {
      if (unref(computedFinanceStepCompleted)) {
        isTaxApplicable.value = false;
      }

      if (taxSettings?._id && !taxSettings?.applicable.length) {
        unref(formData).tax_settings = {
          _id: null,
          name: "",
        };
      }
    }
  },
  { deep: true },
);

watch(isTaxApplicable, async (value) => {
  const response = await taxApi.index({
    params: {
      roles: unref(user)?.basicInfo?.role,
    },
  });

  taxes.value = response?.data?.data;

  if (!value) {
    if (unref(formData).tax_settings._id !== null) {
      unref(formData).tax_settings = {
        _id: null,
        name: "",
      };
    }
  } else if (unref(taxes).length == 1 && value) {
    unref(formData).tax_settings = unref(taxes)[0];
  }
});

const emit = defineEmits(["update:modelValue"]);

const computedFormData = computed({
  get: function () {
    return props.modelValue;
  },
  set: function (updatedFormData) {
    emit("update:modelValue", updatedFormData);
  },
});

watch(
  formData,
  (data) => {
    computedFormData.value = data;
  },
  {
    immediate: true,
  },
);

watchEffect(() => {
  let taxSettings = unref(formData).tax_settings;

  if (taxSettings?._id == null) {
    unref(formData).tax_settings = {
      _id: null,
      name: "",
      ...taxSettings,
    };
  }

  initTaxFilter(unref(user)?.basicInfo?.role);
});

onMounted(() => {
  let role = unref(user)?.basicInfo?.role;

  if (role === "seller" && !unref(computedFinanceStepCompleted)) {
    isTaxApplicable.value = true;
  }
});
</script>
